import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { ReconciliationService } from "@/api";
import type { AnnualConfirmation } from "@/types/annualConfirmation";

// annualConfirmationKeys is org-scoped like the other reconciliation keys: a
// Clerk org switch does not clear the query cache. The year is part of the key
// so each selected year caches independently; "default" is the years-only
// request the dialog makes before a year is known.
export const annualConfirmationKeys = {
  all: ["reconciliation", "annual-confirmation"] as const,
  detail: (
    orgId: string | null | undefined,
    debtorId: string,
    year: number | undefined,
  ) =>
    [
      ...annualConfirmationKeys.all,
      orgId ?? null,
      debtorId,
      year ?? "default",
    ] as const,
};

/**
 * useAnnualConfirmation fetches the annual confirmation numbers of a parent
 * debtor account (#1862): the years with eligible invoices and, for `year`,
 * the per-child monthly parent share and the total. `placeholderData:
 * keepPreviousData` keeps the previous year's preview on screen while the
 * newly selected year loads.
 */
export function useAnnualConfirmation(
  debtorId: string | undefined,
  year: number | undefined,
) {
  const { orgId } = useAuth();
  return useQuery<AnnualConfirmation>({
    queryKey: annualConfirmationKeys.detail(orgId, debtorId ?? "", year),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsAnnualConfirmation(
        debtorId ?? "",
        year,
      ),
    enabled: !!debtorId,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
}
